if (!customElements.get("td-accordion")) {
  class Accordion extends HTMLElement {
    constructor() {
      super();
      this.header = this.querySelector('.accordion-header');
      this.content = this.querySelector('.accordion-content');
      this.openNestedAccordions = null;
      this.resizeDebounceTimeout = null;
    }

    connectedCallback() {
      if (!this.isConnected) return;
      
      // Bind event handlers and set up listeners
      this.boundToggleAccordion = this.toggleAccordion.bind(this);
      this.boundHandleNestedToggle = this.handleNestedToggle.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      
      this.header.addEventListener('click', this.boundToggleAccordion);
      this.addEventListener('accordionToggle', this.boundHandleNestedToggle);
      window.addEventListener('resize', this.boundHandleResize);
      
      // Check if this accordion should be open by default
      if (this.hasAttribute('default-open')) {
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => this.toggleAccordion(), 0);
      }
    }

    disconnectedCallback() {
      this.header?.removeEventListener('click', this.boundToggleAccordion);
      this.removeEventListener('accordionToggle', this.boundHandleNestedToggle);
      window.removeEventListener('resize', this.boundHandleResize);
    }

    // Handle window resize
    handleResize() {
      if (this.resizeDebounceTimeout) {
        clearTimeout(this.resizeDebounceTimeout);
      }
      
      this.resizeDebounceTimeout = setTimeout(() => {
        if (this.header.classList.contains('active')) {
          this.updateHeight();
        }
      }, 250);
    }

    // Calculate total height of content and expanded nested accordions
    calculateContentHeight() {
      // Start with the basic content height
      let totalHeight = this.content.scrollHeight;
      
      // Get all expanded direct child accordions
      const directChildAccordions = Array.from(this.content.children)
        .filter(child => 
          child.tagName.toLowerCase() === 'td-accordion' && 
          child.querySelector('.accordion-header.active')
        );
      
      // Add their content heights
      directChildAccordions.forEach(accordion => {
        const accordionContent = accordion.querySelector('.accordion-content');
        if (accordionContent && accordionContent.style.maxHeight) {
          // Use getBoundingClientRect for more accurate height measurement
          totalHeight += accordionContent.getBoundingClientRect().height;
        }
      });
      
      return totalHeight;
    }

    // Handle nested toggle events
    handleNestedToggle(event) {
      // Check if this is a direct child or deeper descendant
      const isDirectChild = Array.from(this.content.children)
        .some(child => child === event.target || child.contains(event.target));
      
      if (!isDirectChild) return;
      
      // Only update if this accordion is already open
      if (this.header.classList.contains('active')) {
        requestAnimationFrame(() => this.updateHeight());
      }
    }

    // Update accordion height
    updateHeight() {
      const height = this.calculateContentHeight();
      requestAnimationFrame(() => {
        this.content.style.maxHeight = height + "px";
        
        // Notify parents of height change
        this.dispatchEvent(new CustomEvent('accordionToggle', {
          bubbles: true,
          detail: { height }
        }));
      });
    }

    // Toggle accordion state
    toggleAccordion(event) {
      // Prevent event from bubbling to parent accordions
      event?.stopPropagation();
      
      const isExpanding = !this.content.style.maxHeight;

      if (isExpanding) {
        // Open accordion
        this.updateHeight();
        this.header.classList.add('active');
        
        // Restore previously open nested accordions if they exist
        if (this.openNestedAccordions && this.openNestedAccordions.length) {
          setTimeout(() => {
            this.openNestedAccordions.forEach(accordion => {
              accordion.toggleAccordion();
            });
            this.openNestedAccordions = null;
            
            // Update height after restoring nested accordions
            this.updateHeight();
          }, 50);
        }
      } else {
        // Store current state of nested accordions before closing
        this.openNestedAccordions = Array.from(
          this.content.querySelectorAll('td-accordion .accordion-header.active')
        ).map(header => header.closest('td-accordion'));
        
        // Close the current accordion
        this.content.style.maxHeight = null;
        this.header.classList.remove('active');
      }

      // Notify parents of height change
      this.dispatchEvent(new CustomEvent('accordionToggle', {
        bubbles: true,
        detail: { height: isExpanding ? this.calculateContentHeight() : 0 }
      }));
    }
  }

  customElements.define('td-accordion', Accordion);
}
